import type React from 'react';
import { twMerge } from 'tailwind-merge';

import type { SlotPropsList } from '#/types';
import { Glow } from './glow';

export type GlowHaloProps = React.PropsWithChildren<{
  /**
   * If true, the avatar will have a glow effect.
   * @default true
   */
  glow?: boolean | 'hover';
  /**
   * The variant of the GlowHalo.
   * @default 'square'
   */
  variant?: 'rounded' | 'circular' | 'square';
  /**
   * If true, the GlowHalo will be interactive.
   * @default false
   */
  interactive?: boolean;
  /**
   * The blur amount of the glow.
   * @default 32
   */
  blur?: number;
  /**
   * The slot props to pass to the component.
   */
  slotProps?: SlotPropsList<['content', 'glow']>;
  /**
   * Additional classes to apply to the root element.
   */
  className?: string;
}>;

function GlowHalo({
  glow = true,
  interactive = false,
  blur = 32,
  slotProps,
  className,
  children,
}: GlowHaloProps) {
  return (
    <div className={twMerge('relative mx-auto group', interactive && 'cursor-pointer', className)}>
      {!!glow && (
        <Glow
          blur={blur}
          color='primary'
          className={twMerge(
            'rounded-(--radius-box) z-0 block m-auto absolute inset-0 h-full w-full',
            interactive && 'cursor-pointer',
            slotProps?.glow?.className,
            glow === 'hover' && 'opacity-0 group-hover:opacity-100'
          )}
        />
      )}
      <span className={twMerge('relative z-10', slotProps?.content?.className)}>{children}</span>
    </div>
  );
}
GlowHalo.displayName = 'GlowHalo';

export { GlowHalo };
